import clsx from 'clsx';
import React, { FC } from 'react';
import { Check } from '@dxb-web-ui-framework/design-system/dist/icons';
import {
  Box,
  Step,
  Stepper,
  StepLabel,
  makeStyles,
  withStyles,
  Orientation,
  StepConnector,
  StepIconProps,
} from '@dxb-web-ui-framework/design-system';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';

export type CustomStepperProps = {
  totalSteps: number;
  stepperConfig: GenericObject[];
  totalStepsCompleted: number;
  size?: 'small' | 'large';
  ariaLabel?: string;
  dataTestID?: string;
  orientation?: Orientation;
  labelOrientation?: Orientation;
  hideInactiveLabelOnMobile?: boolean;
  hideStepperIndicatorOnStepIndex?: number[];
  externalStepperWrapperClasses?: string;
  externalStepperClasses?: string;
};

const ColorlibConnector = withStyles({
  alternativeLabel: {
    top: 25,
  },
  line: {
    height: 3,
    marginLeft: 10,
    marginRight: 10,
    border: 0,
    backgroundColor: (props: any) =>
      props.index === 1
        ? props.completed || props.active
          ? '#FFA20033'
          : '#EAEAF0'
        : props.index === 2
        ? props.completed || props.active
          ? '#A7000033'
          : '#EAEAF0'
        : props.index === 3
        ? props.completed || props.active
          ? '#00664B33'
          : '#EAEAF0'
        : props.completed || props.active
        ? '#004EAD33'
        : '#EAEAF0',
    borderRadius: 1,
  },
})(StepConnector);

const useColorlibStepIconStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#ccc',
    zIndex: 1,
    color: theme.palette.common.white,
    width: 48,
    height: 48,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  active: {
    background: theme.palette.common.black,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },
  completed: {
    background: theme.palette.success.main,
  },
}));

const useStepperStyles = makeStyles(({ spacing }) => ({
  root: {
    padding: spacing(0),
    maxWidth: 464, // Same as the page content
  },
}));

const CustomStepper: FC<CustomStepperProps> = ({
  stepperConfig,
  totalSteps,
  totalStepsCompleted,
  size = 'large',
  dataTestID = 'wizard-flow-stepper',
  ariaLabel,
  orientation = 'horizontal',
  labelOrientation = 'horizontal',
  hideInactiveLabelOnMobile = false,
  hideStepperIndicatorOnStepIndex,
  externalStepperWrapperClasses,
  externalStepperClasses,
}) => {
  const classes = useStepperStyles();
  const { isSmallScreen: isMobile } = useScreenDetector();

  const ColorlibStepIcon = (props: StepIconProps) => {
    const classes = useColorlibStepIconStyles(props);
    const { active, completed } = props;
    const icons: React.ReactElement[] = stepperConfig.map(step => (
      <step.icon color={active || completed ? step.color : 'inherit'} />
    ));

    return (
      <div
        className={clsx(classes.root, {
          [classes.active]: active,
          [classes.completed]: completed,
        })}>
        {completed ? (
          <Check
            color={
              props.icon === 1 ? 'inherit' : props.icon === 2 ? 'inherit' : props.icon === 3 ? 'inherit' : 'inherit'
            }
          />
        ) : (
          icons[(props.icon as number) - 1]
        )}
      </div>
    );
  };

  return (
    <Box
      py={5}
      display={'flex'}
      flexDirection={isMobile ? 'row' : 'unset'}
      justifyContent={isMobile ? 'center' : 'unset'}
      className={externalStepperWrapperClasses}>
      <Stepper
        aria-label={ariaLabel}
        alternativeLabel
        data-testid={dataTestID}
        orientation={orientation}
        activeStep={totalStepsCompleted}
        connector={<ColorlibConnector />}
        classes={{ root: clsx(classes.root, externalStepperClasses) }}>
        {stepperConfig.map((step, index) => (
          <Step key={index} completed={index < totalStepsCompleted} active={index === totalStepsCompleted}>
            <StepLabel StepIconComponent={ColorlibStepIcon}>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export { CustomStepper };
